import { useEffect, useState } from "react";
import DeleteIcon from "src/assets/all-icons/delete-icon.jsx";

import useTranslations from '@/i18n/useTranslations';
import XIcon from "@/shared-components/icons/components/public/XIcon";
import TextInput from '@/shared-components/inputs/components/new/TextInput.jsx';
import ResizableTextarea from '@/shared-components/inputs/components/ResizableTextarea';
import Text from '@/shared-components/text/components/Text';

import "../styles/PollQuizCreationPopup.css";
import "./styles/base.css";
import "./styles/creation.css";

const QuizCreation = ({ onClose, onDelete, onSubmit, initialQuizData }) => {
  const t = useTranslations('common.forms.poll_quiz_creation');
  // Initialize state from initialQuizData, or defaults
  const [quizQuestion, setQuizQuestion] = useState(
    initialQuizData ? initialQuizData.question : ""
  );
  const [quizOptions, setQuizOptions] = useState(
    initialQuizData ? initialQuizData.options : ["", ""]
  );
  const [correctOption, setCorrectOption] = useState(
    initialQuizData ? initialQuizData.correctOption : null
  );
  const [isValid, setIsValid] = useState(() => {
    if (!initialQuizData) return false;
    const filled = initialQuizData.options.filter(opt => opt.trim());
    return (
      filled.length >= 2 &&
      initialQuizData.correctOption != null &&
      initialQuizData.question.trim() !== ""
    );
  });

  // Keep local state in sync when editing an existing quiz
  useEffect(() => {
    if (initialQuizData) {
      setQuizQuestion(initialQuizData.question);
      setQuizOptions(initialQuizData.options);
      setCorrectOption(initialQuizData.correctOption);
      const filled = initialQuizData.options.filter(opt => opt.trim());
      setIsValid(
        filled.length >= 2 &&
        initialQuizData.correctOption != null &&
        initialQuizData.question.trim() !== ""
      );    } else {
      setQuizQuestion("");
      setQuizOptions(["", ""]);
      setCorrectOption(null);
      setIsValid(false);
    }
  }, [initialQuizData]);

  const handleQuestionChange = e => {
    const value = e.target.value.slice(0, 140);
    setQuizQuestion(value);
    // re-validate now including the new question text
    validate(quizOptions, correctOption, value);
  };

  // now also requires a non-empty question
  const validate = (options, correctIdx, questionText = quizQuestion) => {
    const filled = options.filter(opt => opt.trim());
    setIsValid(
      filled.length >= 2 &&
      correctIdx != null &&
      questionText.trim() !== ""
    );
  };
  const handleOptionChange = (idx, value) => {
    const opts = [...quizOptions];
    opts[idx] = value.slice(0, 200);
    setQuizOptions(opts);
    validate(opts, correctOption);
  };

  const handleSetCorrect = idx => {
    setCorrectOption(prev => {
      // if you click the same idx twice, clear selection
      const newCorrect = prev === idx ? null : idx;
      validate(quizOptions, newCorrect);
      return newCorrect;
    });
  };

  const handleAddOption = () => {
    if (quizOptions.length < 4) {
      const opts = [...quizOptions, ""];
      setQuizOptions(opts);
      validate(opts, correctOption);
    }
  };

  const handleRemoveOption = idx => {
    let opts = quizOptions.filter((_, i) => i !== idx);
    let correctIdx = correctOption;
    if (idx === correctOption) correctIdx = null;
    else if (idx < correctOption) correctIdx--;
    setQuizOptions(opts);
    setCorrectOption(correctIdx);
    validate(opts, correctIdx);
  };

  const handleSubmit = () => {
    if (!isValid) return;
    const filledOptions = quizOptions.filter(opt => opt.trim());
    onSubmit({
      // If editing, include the existing quizId; otherwise the manager will assign one
      quizId: initialQuizData?.quizId,
      question: quizQuestion,
      options: filledOptions,
      correctOption
    });
    onClose();
  };

  return (
    <div className="poll-quiz-creation-popup__body">
      <div className="generic-popup__header poll-quiz-creation-popup__header">
        <Text className="generic-popup__header-text-theme poll-quiz-creation-popup__title">
          {initialQuizData ? t('edit_quiz') : t('create_quiz')}
        </Text>
        {onDelete && (
          <DeleteIcon className="poll-quiz-creation-popup__delete" onClick={onDelete} />
        )}
        <XIcon className="generic-popup__close" onClick={onClose} />
      </div>

      <div className="poll-quiz-creation-popup__form">

        <ResizableTextarea
          className="quiz-question-input"
          placeholder={t('quiz_question_placeholder')}
          element="body-text_article"
          maxChar={140}
          ignoreHeader={true}
          value={quizQuestion}
          onChange={handleQuestionChange}
          onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
          onKeyPress={e => { if (e.key === 'Enter') e.preventDefault(); }}
          onBeforeInput={e => {
            // modern browsers fire this for any “insertLineBreak” attempt
            if (e.inputType === 'insertLineBreak') e.preventDefault();
          }}
        />

        <div className="quiz-creation__content">
          <div className="quiz-options-container">
            {quizOptions.map((opt, idx) => (
              <div key={idx} className="quiz-option">
                <div
                  className={`quiz-option-input-wrapper ${
                    correctOption === idx ? "selected" : ""
                  }`}
                >                  <span className="quiz-option-circle">
                    {String.fromCharCode(65 + idx)}
                  </span>
                  <TextInput
                    type="textarea"
                    label={t('option_label', { number: idx + 1 })}
                    placeholder=""
                    className="quiz-option-input"
                    fixedPlaceholder={false}
                    value={opt}
                    onChange={e => handleOptionChange(idx, e.target.value)}
                    maxLength={150}
                    rows={1}
                    autoResize={true}
                    maxRows={3} // limit to 3 rows maximum
                  />
                  <button
                    type="button"
                    className={`quiz-option-select ${correctOption === idx ? "selected" : ""}`}
                    onClick={() => handleSetCorrect(idx)}
                    aria-label={t('mark_correct_option', { option: String.fromCharCode(65 + idx) })}
                  >
                    <span className="quiz-option-select-circle" />
                  </button>
                </div>
                {quizOptions.length > 2 && idx >= 2 && (
                  <DeleteIcon
                    className="quiz-option-delete"
                    onClick={() => handleRemoveOption(idx)}
                  />
                )}
              </div>
            ))}
          </div>

          {quizOptions.length < 4 && (
            <div className="quiz-add-option-container">
              <button className="quiz-add-option" onClick={handleAddOption}>
                {t('add_option')}
              </button>
            </div>
          )}
          
          <button
            className="quiz-submit-button"
            onClick={handleSubmit}
            disabled={!isValid}
          >
            {t('submit_quiz')}
          </button>
          
          <p className="quiz-warning">
            {isValid ? "" : t('quiz_incomplete')}
          </p>

        </div>
      </div>
    </div>
  );
};

export default QuizCreation;
